import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useHeader } from './../../components/HeaderContext';
import { useAxiosPrivate } from "./../../hooks/useAxiosPrivate";
import useAuth from "./../../hooks/useAuth.js";
import { useAlert } from './../../components/AlertContext.jsx';
import { format } from "date-fns";

const TestOrderEdit = () => {
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    const { auth } = useAuth();
    const axiosPrivate = useAxiosPrivate();
    const { triggerAlert } = useAlert();
    const { setHeader } = useHeader();

    const ReturnPage = "/test_orders";
    const last_modified_date = format(Date(), "dd-MM-yyyy HH:mm:ss");
    const last_modified_user_id = auth.user_id ? auth.user_id : 0;

    // Initial form state for test order header
    const initialFormData = {
        test_order_header_id: '',
        test_order_no: '',
        description: '',
        source_type: '',
        source_no: '',
        source_line_no: '',
        source_status: '',
        test_date: '',
        lot_no: '',
        test_destination_no: '',
        test_destination_type: '',
        test_plan_no: '',
        test_plan_version_no: '',
        status: '',
        no_of_units_to_check: '',
        random_sample_quantity: '',
        quantity_to_test: '',
        no_of_units: '',
        operator: '',
        company: '',
        last_modified_date: last_modified_date,
        last_modified_user_id: last_modified_user_id
    };

    // Initial state for test order lines
    const initialLinesData = [];

    const [formData, setFormData] = useState(initialFormData);
    const [linesData, setLinesData] = useState(initialLinesData);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        console.log("Edit page location state:", location.state);
    }, [location.state]);

    // Set page header
    useEffect(() => {
        setHeader("Edit Test Order");
    }, [setHeader]);

    // Fetch data when component mounts - using the combined endpoint
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await axiosPrivate.get(`${baseURL}/test_orders_edit/${id}`);
                const { header, lines } = response.data;

                // Format the last modified date if it exists
                const formattedDate = header.last_modified_date
                    ? `${header.last_modified_date.substring(0, 10)} ${header.last_modified_date.substring(11, 19)}`
                    : last_modified_date;

                setFormData({
                    ...header,
                    last_modified_date: formattedDate,
                    last_modified_user_id: header.last_modified_user_id || last_modified_user_id
                });

                setLinesData(lines || []);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching test order data:', error);
                setError('Failed to load test order data');
                setLoading(false);
                triggerAlert('Failed to load test order data', 'error');
            }
        };

        fetchData();
    }, [id, axiosPrivate, baseURL, triggerAlert]);

    const handleSubmitEdit = async (e) => {
        e.preventDefault();
        try {
            const updatedFormData = {
                ...formData,
                last_modified_user_id: last_modified_user_id
            };

            // Use the combined update endpoint
            await axiosPrivate.put(`${baseURL}/test-orders-with-lines/${id}`, {
                header: updatedFormData,
                lines: linesData
            });

            triggerAlert('Record Saved!', 'success');
            navigate(ReturnPage);
        } catch (error) {
            console.error('Error updating test order:', error);
            const message = error.response?.data?.err?.originalError?.info?.message
                ? `Error saving record: ${error.response.data.err.originalError.info.message}`
                : 'Error saving record';
            triggerAlert(message, 'error');
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Function to handle adding a new line
    const handleAddLine = () => {
        const newLine = {
            test_order_lines_id: '',
            test_order_no: formData.test_order_no,
            line_no: linesData.length + 1,
            test_property_set_code: '',
            description: '',
            company: formData.company
        };

        setLinesData(prev => [...prev, newLine]);
    };

    // Function to handle line changes
    const handleLineChange = (index, name, value) => {
        const updatedLines = [...linesData];
        updatedLines[index] = {
            ...updatedLines[index],
            [name]: value
        };
        setLinesData(updatedLines);
    };

    // Function to handle line deletion
    const handleDeleteLine = (index) => {
        const updatedLines = linesData.filter((_, i) => i !== index);
        // Recalculate line numbers if needed
        const reindexedLines = updatedLines.map((line, i) => ({
            ...line,
            line_no: i + 1
        }));
        setLinesData(reindexedLines);
    };

    if (loading) return <div className="text-center mt-4 app_font1">Loading...</div>;
    if (error) return <div className="alert alert-danger mt-4 app_font1">{error}</div>;

    // Enhanced field configuration with layout control
    const fieldGroups = [
        {
            title: "Test Order Information",
            layout: 'flex',
            fields: [
                { name: 'test_order_header_id', label: 'Test Order Header ID', type: 'text', disabled: true, size: 1, hidden: true },
                { name: 'test_order_no', label: 'Test Order No', type: 'text', required: true, size: 2 },
                { name: 'description', label: 'Description', type: 'text', size: 4 },

            ]
        },
        {
            layout: 'flex',
            fields: [
                { name: 'source_type', label: 'Source Type', type: 'text', size: 2 },
                { name: 'source_no', label: 'Source No', type: 'text', size: 2 },
                { name: 'source_line_no', label: 'Source Line No', type: 'text', size: 2 },
                { name: 'source_status', label: 'Source Status', type: 'text', size: 2 }
            ]
        },
        {
            layout: 'flex',
            fields: [
                { name: 'test_date', label: 'Test Date', type: 'date', size: 2 },
                { name: 'lot_no', label: 'Lot No', type: 'text', size: 2 },
            ]
        },
        {
            title: "Destination Information",
            layout: 'flex',
            fields: [
                { name: 'test_destination_no', label: 'Test Destination No', type: 'text', size: 2 },
                { name: 'test_destination_type', label: 'Test Destination Type', type: 'text', size: 2 },
            ]
        },
        {
            title: "Test Plan Details",
            layout: 'flex',
            fields: [
                { name: 'test_plan_no', label: 'Test Plan No', type: 'text', size: 2 },
                { name: 'test_plan_version_no', label: 'Test Plan Version No', type: 'text', size: 2 },
                { name: 'status', label: 'Status', type: 'text', size: 2 },
            ]
        },
        {
            title: "Quantity Information",
            layout: 'flex',
            fields: [
                { name: 'no_of_units_to_check', label: 'Units to Check', type: 'number', size: 2 },
                { name: 'random_sample_quantity', label: 'Random Sample Qty', type: 'number', size: 2 },
                { name: 'quantity_to_test', label: 'Quantity to Test', type: 'number', size: 2 },
                { name: 'no_of_units', label: 'No of Units', type: 'number', size: 2 },
            ]
        },
        {
            layout: 'flex',
            fields: [
                { name: 'operator', label: 'Operator', type: 'text', size: 2 },
                { name: 'company', label: 'Company', type: 'text', size: 2 },
            ]
        },
        {
            title: "System Information",
            layout: 'flex',
            fields: [
                { name: 'last_modified_date', label: 'Last Modified', type: 'text', disabled: true, size: 2 },
                { name: 'last_modified_user_id', label: 'Modified By', type: 'text', disabled: true, size: 1 }
            ]
        }
    ];

    const renderField = (field) => {
        if (field.hidden) return null;

        const sizeClass = `col-md-${field.size || 1}`;
        const inputClass = `form-control form-control-sm app_font1 ${field.disabled ? 'bg-light' : ''}`;
        const labelClass = `form-label app_font1 ${field.required ? 'required' : ''}`;

        return (
            <div className={`${sizeClass} mb-3`} key={field.name}>
                {/* Label - not shown for checkboxes */}
                {field.type !== 'checkbox' && (
                    <label className={labelClass}>
                        {field.label}
                    </label>
                )}

                {/* SELECT Input */}
                {field.type === 'select' ? (
                    <select
                        className={inputClass}
                        name={field.name}
                        value={formData[field.name] || ''}
                        onChange={handleInputChange}
                        disabled={field.disabled}
                    >
                        {field.placeholder && <option value="">{field.placeholder}</option>}
                        {field.options?.map(option => (
                            typeof option === 'string'
                                ? <option key={option} value={option}>{option}</option>
                                : <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                ) :

                    /* CHECKBOX Input */
                    field.type === 'checkbox' ? (
                        <div className="form-check">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                name={field.name}
                                checked={!!formData[field.name]}
                                onChange={handleInputChange}
                                disabled={field.disabled}
                                id={`checkbox-${field.name}`}
                            />
                            <label className="form-check-label app_font1" htmlFor={`checkbox-${field.name}`}>
                                {field.label}
                            </label>
                        </div>
                    ) :

                        /* TEXTAREA Input */
                        field.type === 'textarea' ? (
                            <textarea
                                className={inputClass}
                                name={field.name}
                                value={formData[field.name] || ''}
                                onChange={handleInputChange}
                                disabled={field.disabled}
                                rows={field.rows || 3}
                                placeholder={field.placeholder}
                            />
                        ) :

                            /* DATE Input */
                            field.type === 'date' ? (
                                <input
                                    type="date"
                                    className={inputClass}
                                    name={field.name}
                                    value={formData[field.name] || ''}
                                    onChange={handleInputChange}
                                    disabled={field.disabled}
                                />
                            ) :

                                /* NUMBER Input */
                                field.type === 'number' ? (
                                    <input
                                        type="number"
                                        className={inputClass}
                                        name={field.name}
                                        value={formData[field.name] || ''}
                                        onChange={handleInputChange}
                                        disabled={field.disabled}
                                        min={field.min}
                                        max={field.max}
                                        step={field.step}
                                        placeholder={field.placeholder}
                                    />
                                ) :

                                    /* DEFAULT Text Input */
                                    (
                                        <input
                                            type={field.type || 'text'}
                                            className={inputClass}
                                            name={field.name}
                                            value={formData[field.name] || ''}
                                            onChange={handleInputChange}
                                            disabled={field.disabled}
                                            placeholder={field.placeholder}
                                            maxLength={field.maxLength}
                                        />
                                    )}

                {/* Field description/help text */}
                {field.helpText && (
                    <small className="form-text text-muted app_font1">{field.helpText}</small>
                )}
            </div>
        );
    };

    const handleCancel = () => {
        console.log("Current pagination state:", {
            page: location.state?.pagination?.page
        });

        navigate(ReturnPage, {
            state: {
                pagination: {
                    page: location.state?.pagination?.page || 0,
                    pageSize: location.state?.pagination?.pageSize || 20,
                    sortField: location.state?.pagination?.sortField || "test_order_no",
                    sortOrder: location.state?.pagination?.sortOrder || "asc"
                }
            }
        });
    };

    return (
        <div className="d-flex align-items-start mt-1">
            <div className="p-2 rounded w-100">
                <form onSubmit={handleSubmitEdit}>
                    {fieldGroups.map((group, groupIndex) => (
                        <div key={groupIndex} className={group.title ? 'mt-4' : ''}>
                            {group.title && <h5 className="border-bottom pb-2">{group.title}</h5>}
                            <div className={`row ${group.layout === 'stack' ? 'flex-column' : ''}`}>
                                {group.fields.map(field => renderField(field))}
                            </div>
                        </div>
                    ))}

                    {/* Test Order Lines Table */}
                    <div className="mt-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <h5 className="border-bottom pb-2 app_font1">Test Order Lines</h5>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-primary app_font1"
                                onClick={handleAddLine}
                            >
                                Add Line
                            </button>
                        </div>

                        <div className="table-responsive mt-3">
                            <table className="table table-sm table-striped table-bordered">
                                <thead>
                                    <tr>
                                        <th className="app_font1">Line No</th>
                                        <th className="app_font1">Test Property Set Code</th>
                                        <th className="app_font1">Description</th>
                                        <th className="app_font1">Company</th>
                                        <th className="app_font1">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {linesData.length === 0 ? (
                                        <tr>
                                            <td colSpan="5" className="text-center app_font1">No lines found</td>
                                        </tr>
                                    ) : (
                                        linesData.map((line, index) => (
                                            <tr key={index}>
                                                <td className="app_font1">{line.line_no}</td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm app_font1"
                                                        value={line.test_property_set_code || ''}
                                                        onChange={(e) => handleLineChange(index, 'test_property_set_code', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm app_font1"
                                                        value={line.description || ''}
                                                        onChange={(e) => handleLineChange(index, 'description', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control form-control-sm app_font1"
                                                        value={line.company || ''}
                                                        onChange={(e) => handleLineChange(index, 'company', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger app_font1"
                                                        onClick={() => handleDeleteLine(index)}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="mt-3">
                        <button type='submit' className="btn btn-primary me-2 app_font1">Save</button>
                        <button
                            type="button"
                            className="btn btn-secondary app_font1"
                            onClick={handleCancel}
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default TestOrderEdit;